import { useState, useContext, useRef, useEffect, memo } from "react";
import { parseISO, format } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import { IconButton, Button, Tooltip, useTheme, Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import TicketMessagesDialog from "../TicketMessagesDialog";
import { toast } from "react-toastify";
import EyeIcon from "../../assets/icon/EyeIcon";
import TicketOptionsMenu from "../TicketOptionsMenu";
import MoreIcon from "../../assets/icon/MoreIcon";
import { Instagram, QuestionAnswer, WhatsApp } from "@material-ui/icons";
import WhatsAppIconCloud from "../../assets/icon/WhatsAppIconCloud";
import MessengerIcon from "../../assets/icon/MessengerIcon";
import WhatsAppIcon from "../../assets/icon/WhatsAppIcon";

const FILE_TYPES = {
  image: { emoji: "📷 Imagen", extensions: ["jpg", "jpeg", "png", "gif", "webp"] },
  video: { emoji: "🎥 Video", extensions: ["mp4", "avi", "mov", "webm", "mkv"] },
  audio: { emoji: "🎵 Audio", extensions: ["mp3", "wav", "ogg", "m4a", "aac"] },
  pdf: { emoji: "📄 PDF", extensions: ["pdf"] },
  document: { emoji: "📄 Documento", extensions: ["doc", "docx", "txt", "rtf", "xls", "xlsx"] },
};
const DEFAULT_FILE = "📎 Archivo";

const AVATAR_COLORS = [
  "#5C6BC0", "#26A69A", "#EC407A", "#7E57C2",
  "#42A5F5", "#66BB6A", "#FFA726", "#26C6DA",
  "#8D6E63", "#78909C", "#AB47BC", "#29B6F6",
];

const getFileTypeFromText = (text) => {
  const lower = text.toLowerCase();
  for (const type of Object.values(FILE_TYPES)) {
    if (type.extensions.some((ext) => lower.includes(`.${ext}`))) {
      return type.emoji;
    }
  }
  return null;
};

const getAvatarColor = (name) => {
  if (!name) return "#90A4AE";
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getContactInitials = (contact) => {
  if (!contact || !contact.name) return "?";
  const names = contact.name.trim().split(" ");
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

/**
 * ========== UI OPTIMISTA PARA OPERACIONES DE TICKETS ==========
 * 
 * Este hook implementa UI optimista para operaciones críticas de tickets:
 * - Aceptar conversaciones (pending → open)
 * - Resolver/Cerrar conversaciones (open → closed)  
 * - Rechazar conversaciones (open/pending → pending sin usuario)
 * - Marcar como no leído (unreadMessages: 0 → 1)
 * 
 * FLUJO OPTIMISTA:
 * 1. 🚀 Actualización inmediata en UI (sin esperar backend)
 * 2. 📡 Confirmación con backend en segundo plano
 * 3. ✅ Sincronización con datos reales del servidor
 * 4. 🔄 Rollback automático si hay errores
 * 
 * BENEFICIOS:
 * - Respuesta instantánea en la interfaz
 * - Experiencia fluida sin esperas
 * - Movimiento automático entre listas
 * - Rollback automático en caso de errores
 * - Consistencia de datos garantizada
 */
// Hook personalizado para operaciones optimistas de tickets
const useOptimisticTicketOperations = (ticketsContext, listStatus, user, localDispatch) => {
  const { updateTicketInList, removeTicketFromList, setTicketsList } = ticketsContext;
  
  const operations = {
    // Aceptar ticket optimísticamente
    acceptTicket: (ticketId, userId, userData, originalTicket) => {
      console.log(`🚀 UI Optimista - Aceptando ticket ${ticketId}...`);
      
      // Crear el ticket actualizado con los nuevos datos
      const updatedTicket = {
        ...originalTicket,
        status: "open",
        userId: userId,
        user: userData,
        updatedAt: new Date().toISOString(),
        optimistic: true // Marcar como optimista
      };
      
      // 1. Actualizar contexto global
      updateTicketInList(updatedTicket);
      
      // 2. Actualizar lista local inmediatamente para transición instantánea
      if (localDispatch) {
        localDispatch({
          type: "UPDATE_TICKET",
          payload: updatedTicket,
          oldStatus: "pending",
          oldUserId: null,
          currentUserId: userId,
          userProfile: user?.profile,
          userQueues: user?.queues?.map(q => q.id) || [],
          listStatus: listStatus,
          withUnreadMessages: false,
          wasStatusChanged: true,
          isPendingToOpenTransition: true, // Flag clave para la transición
          isOptimisticUpdate: true // Marcar como actualización optimista
        });
      }
      
      console.log(`✅ Ticket ${ticketId} actualizado optimísticamente de pending a open`);
    },
    
    // Resolver/cerrar ticket optimísticamente
    resolveTicket: (ticketId, originalTicket) => {
      console.log(`🚀 UI Optimista - Cerrando ticket ${ticketId}...`);
      
      // Crear el ticket actualizado
      const updatedTicket = {
        ...originalTicket,
        status: "closed",
        updatedAt: new Date().toISOString(),
        optimistic: true
      };
      
      // 1. Actualizar contexto global
      updateTicketInList(updatedTicket);
      
      // 2. Actualizar lista local para remoción inmediata
      if (localDispatch) {
        localDispatch({
          type: "UPDATE_TICKET",
          payload: updatedTicket,
          oldStatus: originalTicket.status,
          oldUserId: originalTicket.userId,
          currentUserId: user?.id,
          userProfile: user?.profile,
          userQueues: user?.queues?.map(q => q.id) || [],
          listStatus: listStatus,
          withUnreadMessages: false,
          wasStatusChanged: true,
          isOptimisticUpdate: true
        });
      }
      
      console.log(`✅ Ticket ${ticketId} cerrado optimísticamente`);
    },
    
    // Rechazar ticket optimísticamente
    rejectTicket: (ticketId, originalTicket) => {
      console.log(`🚀 UI Optimista - Rechazando ticket ${ticketId}...`);
      
      // Crear el ticket actualizado (vuelve a pending sin usuario)
      const updatedTicket = {
        ...originalTicket,
        status: "pending",
        userId: null,
        user: null,
        updatedAt: new Date().toISOString(),
        optimistic: true
      };
      
      // 1. Actualizar contexto global
      updateTicketInList(updatedTicket);
      
      // 2. Actualizar lista local para transición inmediata
      if (localDispatch) {
        localDispatch({
          type: "UPDATE_TICKET",
          payload: updatedTicket,
          oldStatus: originalTicket.status,
          oldUserId: originalTicket.userId,
          currentUserId: user?.id,
          userProfile: user?.profile,
          userQueues: user?.queues?.map(q => q.id) || [],
          listStatus: listStatus,
          withUnreadMessages: false,
          wasStatusChanged: true,
          isOptimisticUpdate: true
        });
      }
      
      console.log(`✅ Ticket ${ticketId} rechazado optimísticamente`);
    },
    
    // Marcar como no leído optimísticamente
    markAsUnread: (ticketId, originalTicket) => {
      console.log(`🚀 UI Optimista - Marcando ticket ${ticketId} como no leído...`);
      
      // Crear el ticket actualizado
      const updatedTicket = {
        ...originalTicket,
        unreadMessages: 1,
        optimistic: true
      };
      
      // 1. Actualizar contexto global
      updateTicketInList(updatedTicket);
      
      // 2. Actualizar lista local
      if (localDispatch) {
        localDispatch({
          type: "UPDATE_TICKET",
          payload: updatedTicket,
          oldStatus: originalTicket.status,
          oldUserId: originalTicket.userId,
          currentUserId: user?.id,
          userProfile: user?.profile,
          userQueues: user?.queues?.map(q => q.id) || [],
          listStatus: listStatus,
          withUnreadMessages: false,
          isOptimisticUpdate: true
        });
      }
      
      console.log(`✅ Ticket ${ticketId} marcado como no leído optimísticamente`);
    },
    
    // Confirmar operación con datos del backend
    confirmOperation: (ticketData) => {
      console.log(`✅ Confirmando operación para ticket ${ticketData.id}`);
      
      // Actualizar con datos reales del backend
      updateTicketInList({
        ...ticketData,
        optimistic: false // Quitar marca optimista
      });
    },
    
    // Revertir operación en caso de error
    rollbackOperation: (ticketId, originalTicket) => {
      console.log(`🔄 Revirtiendo operación para ticket ${ticketId}`);
      
      // Restaurar estado original
      const restoredTicket = {
        ...originalTicket,
        optimistic: false
      };
      
      // 1. Restaurar en contexto global
      updateTicketInList(restoredTicket);
      
      // 2. Restaurar en lista local
      if (localDispatch) {
        localDispatch({
          type: "UPDATE_TICKET",
          payload: restoredTicket,
          oldStatus: restoredTicket.status, // Mantener el mismo estado
          oldUserId: restoredTicket.userId,
          currentUserId: user?.id,
          userProfile: user?.profile,
          userQueues: user?.queues?.map(q => q.id) || [],
          listStatus: listStatus,
          withUnreadMessages: false,
          isRollback: true // Flag especial para rollback
        });
      }
      
      console.log(`✅ Ticket ${ticketId} restaurado a su estado original`);
    }
  };
  
  return operations;
};

const useStyles = makeStyles((theme) => ({
  ticket: {
    cursor: "pointer",
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    padding: 0,
    margin: 0,
    borderRadius: 12,
    overflow: "hidden",
    color: theme.palette.textPrimary,
    background: theme.palette.tabHeaderBackground,
    boxShadow: "none",
    border: `1px solid ${theme.mode === "light" ? "rgba(27, 27, 24, 0.08)" : "rgba(255, 255, 255, 0.08)"}`,
    contain: "content",
    transition: "background-color 0.15s ease, border-color 0.15s ease",
    "& .MuiIconButton-root": {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
      },
    },
    "&:hover": {
      background: theme.mode === "light" ? "#F7F6F3" : theme.palette.optionsBackground,
      borderColor: theme.mode === "light" ? "rgba(27, 27, 24, 0.12)" : "rgba(255, 255, 255, 0.12)",
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      background: theme.mode === "light" ? "rgba(241, 176, 0, 0.10)" : "rgba(255, 227, 151, 0.12)",
      borderColor: theme.mode === "light" ? "rgba(241, 176, 0, 0.55)" : "rgba(255, 227, 151, 0.4)",
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    },
  },
  ticketTop: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "5px 10px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      gap: 8,
      padding: "4px 8px",
    },
  },
  ticketAvatarColumn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    alignSelf: "center",
  },
  ticketContentColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  ticketHeaderRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    minWidth: 0,
    minHeight: 22,
  },
  ticketMessageRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: 0,
    minHeight: 20,
  },
  ticketTopActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 2,
    flex: "0 0 auto",
    height: 26,
    "& $iconButton": {
      color: theme.palette.messageIcons,
    },
  },
  postponeChip: {
    height: 22,
    maxWidth: 168,
    fontSize: "0.68rem",
    fontWeight: 600,
    backgroundColor: theme.mode === "light" ? "rgba(255, 167, 38, 0.16)" : "rgba(255, 167, 38, 0.28)",
    color: theme.mode === "light" ? "#9a6400" : "#ffcc80",
    "& .MuiChip-label": {
      paddingLeft: 8,
      paddingRight: 8,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  verticalDivider: {
    width: 1,
    height: 18,
    flexShrink: 0,
    backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.10)" : "rgba(255, 255, 255, 0.12)",
    margin: "0 4px",
  },
  acceptButton: {
    color: "#1B9E3F",
    fontWeight: 600,
    fontSize: 13,
    padding: "0 8px",
    minWidth: 0,
    minHeight: 26,
    textTransform: "none",
    letterSpacing: 0,
    lineHeight: 1.2,
    "&:hover": {
      backgroundColor: "rgba(27, 158, 63, 0.08)",
    },
    "&.Mui-disabled": {
      color: "rgba(27, 158, 63, 0.5)",
    },
  },
  lastMessageTime: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.headerText,
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    letterSpacing: 0,
    minWidth: 48,
    justifyContent: "flex-end",
  },
  unreadBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: theme.mode === "light" ? "#1B1B18" : "#F3F3F3",
    color: theme.mode === "light" ? "#FFFFFF" : "#1B1B18",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
    lineHeight: 1,
    flexShrink: 0,
  },
  channelIcon: {
    width: 26,
    height: 26,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": {
      fontSize: 18,
    },
  },
  contactName: {
    fontWeight: 600,
    fontSize: 15.5,
    lineHeight: 1.2,
    color: theme.palette.textPrimary,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: 15,
    },
  },
  contactNameUnread: {
    fontWeight: 700,
  },
  contactLastMessage: {
    color: theme.palette.headerText,
    fontSize: 14.5,
    lineHeight: 1.25,
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
    },
  },
  avatarContainer: {
    display: "inline-flex",
    flexShrink: 0,
    position: "relative",
  },
  assignedUserAvatar: {
    position: "absolute",
    bottom: -1,
    right: -1,
    width: 18,
    height: 18,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 700,
    backgroundColor: theme.mode === "light" ? "#FFFFFF" : "#1E1E1E",
    color: theme.mode === "light" ? "#167A3A" : "#5ED66D",
    border: `1.5px solid ${theme.mode === "light" ? "#FFFFFF" : theme.palette.tabHeaderBackground}`,
    zIndex: 1,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: "#E0E0E0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      width: 35,
      height: 35,
    },
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
  avatarFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    fontWeight: 600,
    color: "white",
    borderRadius: "50%",
    overflow: "hidden",
  },
  iconButton: {
    padding: 2,
    width: 26,
    height: 26,
    borderRadius: "50%",
  },
  listItemAvatar: {
    minWidth: "auto",
    margin: 0,
  },
}));


const TicketListItemCustom = ({ 
  ticket, 
  setUpdate, 
  isSelected, 
  reloadTickets, 
  onSelectTicket, 
  persistAfterRead, 
  handleDrawerOpen,
  listStatus,
  withUnreadMessages,
  currentUserId,
  dispatch // Dispatch local de la lista
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isDisappearing, setIsDisappearing] = useState(false);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [optionsMenuMounted, setOptionsMenuMounted] = useState(false);
  const ticketOptionsMenuOpen = Boolean(anchorEl);
  const { setCurrentTicket, setTicketsList, updateTicketInList, removeTicketFromList } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const optimisticOps = useOptimisticTicketOperations(
    { updateTicketInList, removeTicketFromList, setTicketsList },
    listStatus,
    user,
    dispatch
  );

  const clickTimeoutRef = useRef(null);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    setLoading(false);
    setIsDisappearing(false);
  }, [ticket?.id]);

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);
  
  const handleSelectTicket = (ticket) => {
    // Prevenir múltiples clics rápidos
    if (isNavigatingRef.current) {
      console.log('🛑 Navegación en progreso, ignorando clic adicional');
      return;
    }
    
    // Limpiar cualquier timeout pendiente
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    // Marcar como navegando
    isNavigatingRef.current = true;
    setLoading(true); // Indicador visual de carga
    
    try {
      console.log(`🎯 Seleccionando ticket ${ticket.uuid}:`, {
        ticketId: ticket.id,
        status: ticket.status,
        unreadMessages: ticket.unreadMessages,
        userId: ticket.userId
      });
      
      const code = uuidv4();
      
      // 🔄 SINCRONIZACIÓN MEJORADA CON EL CONTEXTO
      // 1. Actualizar el contexto ANTES de navegar para asegurar disponibilidad
      setCurrentTicket({ id: ticket.id, uuid: ticket.uuid, code });
      
      // 2. Asegurar que el ticket esté en la lista del contexto
      setTicketsList(prevTickets => {
        const ticketExists = prevTickets.some(t => t.uuid === ticket.uuid);
        if (!ticketExists) {
          console.log(`📝 Agregando ticket ${ticket.uuid} al contexto`);
          return [ticket, ...prevTickets];
        }
        return prevTickets;
      });
  
      // 3. Solo actualizamos en el backend si hay mensajes no leídos
      if (ticket.unreadMessages > 0) {
        // Marcar como leído localmente de inmediato para evitar retrasos en la UI
        const updatedTicket = { ...ticket, unreadMessages: 0 };
        
        // Actualizar en el contexto inmediatamente
        setTicketsList(prevTickets => 
          prevTickets.map(t => 
            t.id === ticket.id ? updatedTicket : t
          )
        );
  
        // Usar el nuevo endpoint específico para marcar como leído (en background)
        api.put(`/tickets/${ticket.id}/mark-read`).catch(err => {
          console.error("Error al marcar mensajes como leídos:", err);
          // Si falla, revertir el cambio local solo si no es persistAfterRead
          if (!persistAfterRead) {
            setTicketsList(prevTickets => 
              prevTickets.map(t => 
                t.id === ticket.id ? ticket : t
              )
            );
          }
        });
      }
      
      // 4. Pequeña pausa para asegurar que el contexto se actualice
      setTimeout(() => {
        try {
          // Navegar después de asegurar la sincronización del contexto
          if (onSelectTicket) {
            console.log(`🚀 Navegando via callback a ticket ${ticket.uuid}`);
            onSelectTicket(ticket.uuid);
          } else {
            console.log(`🚀 Navegando via history.push a ticket ${ticket.uuid}`);
            history.push(`/tickets/${ticket.uuid}`);
          }
          
          // Resetear estados después de navegar
          setTimeout(() => {
            setLoading(false);
            isNavigatingRef.current = false;
          }, 200); // Aumentado ligeramente para dar tiempo a la navegación
          
        } catch (navError) {
          console.error('Error durante la navegación:', navError);
          setLoading(false);
          isNavigatingRef.current = false;
          toast.error('Error al navegar al ticket. Por favor, inténtalo de nuevo.');
        }
      }, 50); // Pausa mínima para sincronización
      
    } catch (error) {
      console.error('Error al seleccionar ticket:', error);
      // Resetear estados en caso de error
      setLoading(false);
      isNavigatingRef.current = false;
      toast.error('Error al abrir el ticket. Por favor, inténtalo de nuevo.');
    }
  };

  const handleOpenTicketOptionsMenu = e => {
    setAnchorEl(e.currentTarget);
    setOptionsMenuMounted(true);
  };

  const handleCloseTicketOptionsMenu = e => {
    setAnchorEl(null);
  };

  const handleResolveTicket = async (e) => {
    handleCloseTicket(ticket.id);
    handleCloseTicketOptionsMenu();
  };

  const truncateText = (text, limit) => {
    if (!text) return "";
    if (text.length > limit) {
      return text.slice(0, limit) + "...";
    }
    return text;
  };

  // Función para procesar lastMessage y manejar casos con 'blob', formato WebChat y otros archivos multimedia
  const processLastMessage = (lastMessage) => {
    if (!lastMessage) return "";
    // 1️⃣ Detectar formato WebChat
    const webChatMatch = lastMessage.match(/\[?Archivo:\s*([^\]]+)\]?/i);
    if (webChatMatch) {
      return getFileTypeFromText(webChatMatch[1]) || DEFAULT_FILE;
    }

    const message = lastMessage.toLowerCase();

    // 2️⃣ Detectar archivos por extensión directa
    const fileType = getFileTypeFromText(message);
    if (fileType) {
      return fileType;
    }

    // 3️⃣ Detectar blobs
    if (message.includes("blob")) {
      return DEFAULT_FILE;
    }

    // 4️⃣ Detectar emojis existentes
    if (message.includes("📷")) return "📷 Imagen";
    if (message.includes("🎥")) return "🎥 Video";
    if (message.includes("🎵")) return "🎵 Audio";
    if (message.includes("📄")) return "📄 Documento";
    if (message.includes("📎")) return DEFAULT_FILE;
    
    return lastMessage;
  };

  const getLastMessageDate = () => {
    return ticket?.lastMessageAt || ticket?.lastMessageCreatedAt || ticket?.updatedAt || ticket?.createdAt;
  };

  const formatLastMessageTime = () => {
    const dateValue = getLastMessageDate();
    if (!dateValue) return "";

    try {
      const messageDate = typeof dateValue === "string" ? parseISO(dateValue) : new Date(dateValue);
      const diffMs = Date.now() - messageDate.getTime();
      if (Number.isNaN(diffMs)) return "";

      const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
      if (diffMinutes < 1) return "Ahora";
      if (diffMinutes < 60) return `Hace ${diffMinutes} min`;

      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) return `Hace ${diffHours} h`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `Hace ${diffDays} d`;

      return messageDate.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "";
    }
  };

  const handleCloseTicket = async (id) => {
    setLoading(true);
    
    // Guardar estado original para rollback
    const originalTicket = { ...ticket };
    
    try {
        console.log(`🚀 UI Optimista - Cerrando ticket ${id}...`);
        
        // ========== ACTUALIZACIÓN OPTIMISTA INMEDIATA ==========
        optimisticOps.resolveTicket(id, originalTicket);
        
        // Inmediatamente ocultar este ticket de la lista actual
        setIsDisappearing(true);
        
        // Remover del contexto y redirigir SIEMPRE cuando se cierra un ticket desde la lista
        // Verificar tanto por ID como por UUID para mayor compatibilidad
        const isCurrentTicket = window.location.pathname.includes(`/tickets/${id}`) || 
                               window.location.pathname.includes(`/tickets/${ticket.uuid}`);
        
        if (isCurrentTicket) {
            console.log(`🔄 Ticket actual cerrado - redirigiendo a lista principal`);
            setCurrentTicket({ id: null, uuid: null, code: null });
            // Dar tiempo para la animación antes de redireccionar
            setTimeout(() => {
                history.push("/tickets");
            }, 300);
        } else {
            // Si no es el ticket actual pero se está cerrando desde la lista,
            // también redirigir para mantener consistencia
            console.log(`🔄 Ticket cerrado desde lista - redirigiendo a lista principal`);
            setTimeout(() => {
                history.push("/tickets");
            }, 300);
        }
        
        toast.success("Chat cerrado correctamente");
        
        // ========== CONFIRMACIÓN CON BACKEND ==========
        console.log(`📡 Confirmando cierre con backend...`);
        const { data } = await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id,
        });
        
        console.log(`✅ Ticket ${id} confirmado cerrado en backend:`, data);
        
        // Confirmar con datos reales del backend
        optimisticOps.confirmOperation(data);
        
        // Resetear estados después de la confirmación
        setTimeout(() => {
            setLoading(false);
            setIsDisappearing(false);
        }, 300);
        
    } catch (err) {
        console.error(`❌ Error cerrando ticket ${id}:`, err);
        
        // ========== ROLLBACK EN CASO DE ERROR ==========
        optimisticOps.rollbackOperation(id, originalTicket);
        
        setLoading(false);
        setIsDisappearing(false);
        toastError(err);
        toast.error("Error al cerrar el chat. Se ha revertido la acción.");
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    
    // Guardar estado original para rollback
    const originalTicket = { ...ticket };
    
    try {
      console.log(`🚀 UI Optimista - Aceptando ticket ${id}...`);
      
      // ========== ACTUALIZACIÓN OPTIMISTA INMEDIATA ==========
      optimisticOps.acceptTicket(id, user?.id, user, originalTicket);
      
      // Actualizar contexto global para navegación
      setCurrentTicket({ id: id, uuid: ticket.uuid, code: ticket.code });
      
      // Inmediatamente ocultar este ticket de la lista actual para animación visual
      setIsDisappearing(true);
      
      // Notificar éxito inmediatamente
      toast.success("Chat aceptado correctamente");
      
      // Navegar al ticket aceptado inmediatamente (sin delay)
      if (onSelectTicket) {
        onSelectTicket(ticket.uuid);
      } else {
        history.push(`/tickets/${ticket.uuid}`);
      }
      
      // ========== CONFIRMACIÓN CON BACKEND ==========
      console.log(`📡 Confirmando aceptación con backend...`);
      const { data } = await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
      
      console.log(`✅ Ticket ${id} confirmado en backend:`, data);
      
      // Confirmar con datos reales del backend
      optimisticOps.confirmOperation(data);
      
      // Resetear estados después de la confirmación
      setTimeout(() => {
        setLoading(false);
        setIsDisappearing(false);
      }, 300);
      
    } catch (err) {
      console.error(`❌ Error aceptando ticket ${id}:`, err);
      
      // ========== ROLLBACK EN CASO DE ERROR ==========
      optimisticOps.rollbackOperation(id, originalTicket);
      
      setLoading(false);
      setIsDisappearing(false);
      toastError(err);
      toast.error("Error al aceptar el chat. Se ha revertido la acción.");
    }
  };

  const handleMarkAsUnread = async (ticketId) => {
    setLoading(true);
    
    // Guardar estado original para rollback
    const originalTicket = { ...ticket };
    
    try {
      console.log(`🚀 UI Optimista - Marcando ticket ${ticketId} como no leído...`);
      
      // ========== ACTUALIZACIÓN OPTIMISTA INMEDIATA ==========
      optimisticOps.markAsUnread(ticketId, originalTicket);
      
      toast.success("Ticket marcado como no leído");
      
      // ========== CONFIRMACIÓN CON BACKEND ==========
      console.log(`📡 Confirmando marca como no leído con backend...`);
      await api.put(`/tickets/${ticketId}/mark-unread`);
      
      console.log(`✅ Ticket ${ticketId} confirmado como no leído en backend`);
      
      // Confirmar con actualización real (opcional, ya que la UI ya está actualizada)
      optimisticOps.confirmOperation({
        id: ticketId,
        unreadMessages: 1,
        optimistic: false
      });
      
    } catch (err) {
      console.error(`❌ Error marcando ticket ${ticketId} como no leído:`, err);
      
      // ========== ROLLBACK EN CASO DE ERROR ==========
      optimisticOps.rollbackOperation(ticketId, originalTicket);
      
      toastError(err);
      toast.error("Error al marcar como no leído. Se ha revertido la acción.");
    } finally {
      setLoading(false);
    }
  };

  const hasUnread = ticket.unreadMessages > 0;
  const queueColor = ticket.queue?.color;

  const formatPostponeUntil = () => {
    if (!ticket?.postponeUntil) return "";
    try {
      const date = typeof ticket.postponeUntil === "string"
        ? parseISO(ticket.postponeUntil)
        : new Date(ticket.postponeUntil);
      if (Number.isNaN(date.getTime())) return "";
      return format(date, "dd/MM/yyyy HH:mm");
    } catch (err) {
      return "";
    }
  };

  return (
    <>
      {openTicketMessageDialog && (
        <TicketMessagesDialog
          open={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticket?.id}
        />
      )}
      {optionsMenuMounted && (
        <TicketOptionsMenu
          ticket={ticket}
          anchorEl={anchorEl}
          menuOpen={ticketOptionsMenuOpen}
          handleClose={handleCloseTicketOptionsMenu}
          handleDrawerOpen={handleDrawerOpen}
          onResolveTicket={handleResolveTicket}
          markAsUnread={handleMarkAsUnread}
          hideEditAndDelete={true}
        />
      )}
      <ListItem
        button={false}
        dense
        disableGutters
        className={classes.ticket}
        onClick={(e) => {
          const isActionButton = e.target.closest(".MuiIconButton-root") || e.target.closest("button");
          if (!isActionButton && !loading) {
            handleSelectTicket(ticket);
          }
        }}
        selected={isSelected}
        style={{
          opacity: isDisappearing ? 0.5 : loading ? 0.7 : 1,
          cursor: loading ? "wait" : "pointer",
        }}
      >
        <div className={classes.ticketTop}>
          <div className={classes.ticketAvatarColumn}>
            <ListItemAvatar className={classes.listItemAvatar}>
              <div className={classes.avatarContainer}>
                <Tooltip title={ticket.queue?.name || "Sin departamento"}>
                  <div
                    className={classes.contactAvatar}
                    style={{
                      border: queueColor ? `3px solid ${queueColor}` : "3px solid transparent",
                    }}
                  >
                    {ticket?.contact?.profilePicUrl ? (
                      <img
                        src={ticket.contact.profilePicUrl}
                        alt={ticket?.contact?.name || "Avatar"}
                        className={classes.avatarImage}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={classes.avatarFallback}
                      style={{
                        backgroundColor: getAvatarColor(ticket?.contact?.name || ""),
                        display: ticket?.contact?.profilePicUrl ? "none" : "flex",
                      }}
                    >
                      {getContactInitials(ticket?.contact)}
                    </div>
                  </div>
                </Tooltip>
                {ticket?.user?.name && (
                  <Tooltip title={ticket.user.name}>
                    <div className={classes.assignedUserAvatar}>
                      {ticket.user.name.charAt(0).toUpperCase()}
                    </div>
                  </Tooltip>
                )}
              </div>
            </ListItemAvatar>
          </div>

          <div className={classes.ticketContentColumn}>
            <div className={classes.ticketHeaderRow}>
              <Typography
                className={`${classes.contactName} ${hasUnread ? classes.contactNameUnread : ""}`}
              >
                {ticket?.contact?.name}
              </Typography>
              <div className={classes.ticketTopActions}>
                <span className={classes.lastMessageTime}>{formatLastMessageTime()}</span>
                <span className={classes.verticalDivider} />
                {ticket.postponeUntil && (
                  <Tooltip title={`Aplazado hasta ${formatPostponeUntil()}`}>
                    <span onClick={(event) => event.stopPropagation()}>
                      <Chip
                        size="small"
                        className={classes.postponeChip}
                        label="Aplazado"
                      />
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={ticket?.whatsapp?.name || (ticket.whatsapp?.channel === "whatsapp" ? "WhatsApp" : "Webchat")}>
                  <span className={classes.channelIcon}>
                    {ticket.whatsapp?.channel === "whatsapp" ? (
                      <WhatsAppIcon fill="#25D366" width="18" height="18" />
                    ) : (
                      ticket.whatsapp?.channel === "whatsapp-api" ? (
                        <WhatsAppIconCloud style={{ color: "#25D366" }} height="20" width="20" />
                      ) : ticket.whatsapp?.channel === "messenger-page" ?
                        <MessengerIcon height="16" width="16" />
                      : ticket.whatsapp?.channel === "instagram-dm" ? (
                        <Instagram style={{ color: "#E1306C", fontSize: 18 }} />
                      ) : (
                        <QuestionAnswer style={{ color: "#2196F3" }} />
                      )
                    )}
                  </span>
                </Tooltip>
                {hasUnread && (
                  <Tooltip title="Mensajes no leídos">
                    <span className={classes.unreadBadge}>
                      {ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
                    </span>
                  </Tooltip>
                )}
                <span className={classes.verticalDivider} />
                <Tooltip title="Ver conversación">
                  <IconButton
                    className={classes.iconButton}
                    disabled={loading}
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenTicketMessageDialog(true);
                    }}
                  >
                    <EyeIcon fill={loading ? "#ccc" : theme.palette.messageIcons} width="18" height="18" />
                  </IconButton>
                </Tooltip>
                {ticket.status === "pending" && (
                  <Button
                    className={classes.acceptButton}
                    disabled={loading}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleAcepptTicket(ticket.id);
                    }}
                  >
                    {loading ? "..." : "Aceptar"}
                  </Button>
                )}
                {ticket.status === "open" && (
                  <Tooltip title="Más opciones">
                    <IconButton
                      className={classes.iconButton}
                      disabled={loading}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenTicketOptionsMenu(event);
                      }}
                    >
                      <MoreIcon fill={loading ? "#ccc" : theme.palette.messageIcons} stroke="1.8" width="18" height="18" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className={classes.ticketMessageRow}>
              <Typography className={classes.contactLastMessage} noWrap>
                {truncateText(processLastMessage(ticket?.lastMessage) || "", 52)}
              </Typography>
            </div>
          </div>
        </div>
      </ListItem>
    </>
  );
};

const areTicketPropsEqual = (prev, next) => (
  prev.isSelected === next.isSelected &&
  prev.ticket?.id === next.ticket?.id &&
  prev.ticket?.status === next.ticket?.status &&
  prev.ticket?.unreadMessages === next.ticket?.unreadMessages &&
  prev.ticket?.lastMessage === next.ticket?.lastMessage &&
  prev.ticket?.lastMessageAt === next.ticket?.lastMessageAt &&
  prev.ticket?.updatedAt === next.ticket?.updatedAt &&
  prev.ticket?.userId === next.ticket?.userId &&
  prev.ticket?.user?.name === next.ticket?.user?.name &&
  prev.ticket?.contact?.name === next.ticket?.contact?.name &&
  prev.ticket?.contact?.profilePicUrl === next.ticket?.contact?.profilePicUrl &&
  prev.ticket?.whatsapp?.channel === next.ticket?.whatsapp?.channel &&
  prev.ticket?.queue?.color === next.ticket?.queue?.color &&
  prev.ticket?.postponeUntil === next.ticket?.postponeUntil
);

export default memo(TicketListItemCustom, areTicketPropsEqual);
